import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { type ExternalToast } from 'sonner';
import { getCurrentUser } from '@features/auth';
import { ENV } from '@constants/env';
import { type LogLevel, type LogEventOptions, type ILogger } from '@types';
import { paintToast } from './paintToast';
import { BETTERSTACK_APP_URL } from '@constants';

/**
 * @deprecated BetterStack is being replaced by Datadog logs (DatadogLoggerService).
 * Both transports run in parallel during the migration; once Datadog is validated
 * in prod, delete this service, betterstackConfigs.ts, and the init token wiring.
 */
export class BetterStackLoggerService implements ILogger {
  private token: string | null = null;

  public init (token: string): void {
    this.token = token;
  }

  private getUserContext (): Record<string, unknown> {
    const user = getCurrentUser();
    if (user == null) {
      return {};
    }

    return {
      userId: user.userId,
      email: user.email,
      role: (user.type ?? []).join(', '),
    };
  }

  private paintToast (
    level: LogLevel,
    message: string,
    toastOptions: ExternalToast = {}
  ): void {
    paintToast(level, message, toastOptions);
  }

  private async sendLog (
    level: LogLevel,
    message: string,
    options: Omit<LogEventOptions, 'context'> & { context?: Record<string, unknown> } = {},
  ): Promise<void> {
    const { context = {}, source, showToast = false, toastOptions = {}, forceLog = false } = options;

    const fullContext = { actor: { ...this.getUserContext() }, ...context };

    if (ENV === 'local' && !forceLog) {
      if (showToast) {
        this.paintToast(level, message, toastOptions);
      }
      return;
    }

    if (this.token == null) {
      return;
    }

    try {
      await axios.post(
        BETTERSTACK_APP_URL,
        { version: 'tendril-v3', env: ENV, level, uuid: uuidv4(), timestamp: new Date().toISOString(), message, source, context: fullContext },
        { headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${this.token}` } },
      );

      if (showToast) {
        this.paintToast(level, message, toastOptions);
      }
    } catch (err) {
      console.warn(`[Logger] Failed to send ${level} log:`, err);
    }
  }

  public info = async (message: string, options?: LogEventOptions): Promise<void> => {
    await this.sendLog('info', message, options);
  };

  public warning = async (message: string, options?: LogEventOptions): Promise<void> => {
    await this.sendLog('warning', message, options);
  };

  public error = async (message: string, options?: LogEventOptions): Promise<void> => {
    await this.sendLog('error', message, options);
  };

  public success = async (message: string, options?: LogEventOptions): Promise<void> => {
    await this.sendLog('success', message, options);
  };
}
